// ---------------------------------------------------------------------------
// SimulationView.tsx — position-sizing simulation page.
//
// Changes from previous version:
//  1. Groups fetched dynamically from useGlobalWatchlists (no hardcoded list).
//  2. Bets table: screener intel columns on the left, sim columns on the right.
//     Event column dropped — event title is a hover tooltip on the question.
//     Market question click navigates to /markets/event/{slug} in a new tab.
//  3. Trader-scores expansion: chevron on far left, expands a YES/NO split
//     table beneath the row. Addresses link to /markets/trader/{address}.
//  4. Performance overview panel (recharts) between ResultsBar and BetsTable:
//     PnL by category and by tag.
//  5. includeTraderScores filter wired through MarketFilters + filtersToParams.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    TextField,
    Slider,
    Switch,
    Tooltip,
    Skeleton,
    Chip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Cell,
    ResponsiveContainer,
} from 'recharts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { SectionCard } from './shared';
import { hoverScrollbar } from '../../../styles/hoverScrollbar';
import { useConvictionGroups } from '../api/useConvictionGroups';
import {
    useConvictionScreener,
    type ConvictionMarket,
    type ConvictionScreenerParams,
} from '../api/useConvictionScreener';
import {
    runSimulation,
    type SimConfig,
    type SizingAlgo,
    type WeightSignal,
    type BetResult,
} from '../utils/simulation';
import {
    toSimMarket,
    daysRunning,
    REFERENCE_PRICE_LABELS,
    type ReferencePrice,
} from '../utils/simMarket';
import {
    MarketFilters,
    filtersToParams,
    DEFAULT_FILTERS,
    type MarketFilterValues,
} from './MarketFilters';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function fmtUsd(v: number | null): string {
    if (v == null) return '—';
    const a = Math.abs(v);
    const s = v < 0 ? '−' : '';
    if (a >= 1_000_000) return `${s}$${(a / 1_000_000).toFixed(2)}M`;
    if (a >= 1_000) return `${s}$${(a / 1_000).toFixed(1)}k`;
    return `${s}$${a.toFixed(0)}`;
}
function fmtSignedUsd(v: number | null): string {
    if (v == null) return '—';
    return (v >= 0 ? '+' : '') + fmtUsd(v);
}
function fmtPct(v: number | null, dp = 0): string {
    if (v == null) return '—';
    return `${(v * 100).toFixed(dp)}%`;
}
function fmtSignedPct(v: number | null, dp = 1): string {
    if (v == null) return '—';
    return `${v >= 0 ? '+' : ''}${(v * 100).toFixed(dp)}%`;
}
function fmtNum(v: number | null, dp = 0): string {
    if (v == null) return '—';
    return v.toLocaleString(undefined, { maximumFractionDigits: dp });
}
function truncAddr(addr: string): string {
    if (addr.length <= 12) return addr;
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

// Stable hue for event-sibling dot
function eventHue(id: string): number {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return h % 360;
}

// ---------------------------------------------------------------------------
// Debounce
// ---------------------------------------------------------------------------

function useDebounced<T>(value: T, delay = 400): T {
    const [d, setD] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setD(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return d;
}

// ---------------------------------------------------------------------------
// Sim UI state → SimConfig
// ---------------------------------------------------------------------------

interface SimUiState {
    bankrollText: string;
    deployMode: 'fraction' | 'absolute';
    deployFraction: number;
    deployAbsoluteText: string;
    algoKind: SizingAlgo['kind'];
    signal: WeightSignal;
    blendBeta: number;
    alpha: number;
    volumeFraction: number;
    fixedDollarsText: string;
    fixedSharesText: string;
    liquidityCapPctText: string;
    eventCapPctText: string;
    redistribute: boolean;
    feePerTradeText: string;
    feeBpsText: string;
    oneBetPerEvent: boolean;
    referencePrice: ReferencePrice;
    minCapitalThresholdText: string;
    minVolumeThresholdText: string;
}

const DEFAULT_SIM_UI: SimUiState = {
    bankrollText: '10000',
    deployMode: 'fraction',
    deployFraction: 1,
    deployAbsoluteText: '10000',
    algoKind: 'proportional',
    signal: 'capital',
    blendBeta: 0.5,
    alpha: 1,
    volumeFraction: 0.01,
    fixedDollarsText: '100',
    fixedSharesText: '100',
    liquidityCapPctText: '',
    eventCapPctText: '',
    redistribute: false,
    feePerTradeText: '0',
    feeBpsText: '0',
    oneBetPerEvent: false,
    referencePrice: 'cw_avg_entry',
    minCapitalThresholdText: '',
    minVolumeThresholdText: '',
};

function num(s: string, d = 0): number {
    const n = Number(s);
    return s.trim() === '' || Number.isNaN(n) ? d : n;
}
function numOrNull(s: string): number | null {
    return s.trim() === '' || Number.isNaN(Number(s)) ? null : Number(s);
}

function buildConfig(ui: SimUiState): SimConfig {
    const bankroll = num(ui.bankrollText, 0);

    let algo: SizingAlgo;
    switch (ui.algoKind) {
        case 'fixedDollars':
            algo = {
                kind: 'fixedDollars',
                dollarsPerMarket: num(ui.fixedDollarsText, 0),
            };
            break;
        case 'fixedShares':
            algo = {
                kind: 'fixedShares',
                sharesPerMarket: num(ui.fixedSharesText, 0),
            };
            break;
        case 'fixedFraction':
            algo = { kind: 'fixedFraction' };
            break;
        case 'volumeFraction':
            algo = {
                kind: 'volumeFraction',
                volumeFraction: ui.volumeFraction,
            };
            break;
        default:
            algo = {
                kind: 'proportional',
                signal: ui.signal,
                blendBeta: ui.blendBeta,
                concentration: ui.alpha,
            };
    }

    const liqPct = numOrNull(ui.liquidityCapPctText);
    const evtPct = numOrNull(ui.eventCapPctText);

    return {
        bankroll,
        deployment:
            ui.deployMode === 'fraction'
                ? { mode: 'fraction', deployFraction: ui.deployFraction }
                : {
                      mode: 'absolute',
                      totalCapital: num(ui.deployAbsoluteText, 0),
                  },
        algo,
        wrappers: {
            liquidityCapFrac: liqPct == null ? null : liqPct / 100,
            maxStakePerEventFrac: evtPct == null ? null : evtPct / 100,
            redistributeCapped: ui.redistribute,
        },
        fees: {
            perTrade: num(ui.feePerTradeText, 0),
            bps: num(ui.feeBpsText, 0),
        },
        oneBetPerEvent: ui.oneBetPerEvent,
        minCapitalThreshold: numOrNull(ui.minCapitalThresholdText),
        minVolumeThreshold: numOrNull(ui.minVolumeThresholdText),
    };
}

// ---------------------------------------------------------------------------
// Sizing controls panel
// ---------------------------------------------------------------------------

const labelSx = {
    fontSize: '9px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'text.secondary',
} as const;
const selectSx = { fontSize: '12px', '& .MuiSelect-select': { py: 0.625 } };
const tinyInput = { width: 88, '& input': { fontSize: '12px', py: 0.75 } };

function Ctl({
    label,
    children,
    width,
}: {
    label: string;
    children: ReactNode;
    width?: number;
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                minWidth: width,
            }}
        >
            <Typography sx={labelSx}>{label}</Typography>
            {children}
        </Box>
    );
}

const ALGO_LABELS: Record<SizingAlgo['kind'], string> = {
    proportional: 'Proportional',
    fixedFraction: 'Fixed fraction',
    volumeFraction: 'Volume fraction',
    fixedDollars: 'Fixed $ / market',
    fixedShares: 'Fixed shares / market',
};

const SIGNAL_LABELS: Record<WeightSignal, string> = {
    capital: 'Dominant capital',
    volume: 'Volume',
    capitalToVolume: 'Capital / Volume',
    blend: 'Blend (cap·vol)',
};

function SimControls({
    ui,
    onChange,
}: {
    ui: SimUiState;
    onChange: (patch: Partial<SimUiState>) => void;
}) {
    const isProp = ui.algoKind === 'proportional';
    const isBlend = isProp && ui.signal === 'blend';
    const budgeted = isProp || ui.algoKind === 'fixedFraction';

    return (
        <SectionCard sx={{ mx: 2, mb: 1.5 }}>
            <Box
                sx={{
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                }}
            >
                {/* Capital + deployment */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-end',
                        gap: 1.5,
                    }}
                >
                    <Ctl label="Bankroll ($)">
                        <TextField
                            size="small"
                            type="number"
                            value={ui.bankrollText}
                            onChange={e =>
                                onChange({ bankrollText: e.target.value })
                            }
                            sx={tinyInput}
                        />
                    </Ctl>

                    <Ctl label="Algo" width={150}>
                        <Select
                            size="small"
                            value={ui.algoKind}
                            onChange={e =>
                                onChange({
                                    algoKind: e.target
                                        .value as SizingAlgo['kind'],
                                })
                            }
                            sx={{ ...selectSx, minWidth: 150 }}
                        >
                            {(
                                Object.keys(ALGO_LABELS) as SizingAlgo['kind'][]
                            ).map(k => (
                                <MenuItem
                                    key={k}
                                    value={k}
                                    sx={{ fontSize: '12px' }}
                                >
                                    {ALGO_LABELS[k]}
                                </MenuItem>
                            ))}
                        </Select>
                    </Ctl>

                    {budgeted && (
                        <>
                            <Ctl label="Deploy by">
                                <Select
                                    size="small"
                                    value={ui.deployMode}
                                    onChange={e =>
                                        onChange({
                                            deployMode: e.target.value as
                                                | 'fraction'
                                                | 'absolute',
                                        })
                                    }
                                    sx={{ ...selectSx, minWidth: 120 }}
                                >
                                    <MenuItem
                                        value="fraction"
                                        sx={{ fontSize: '12px' }}
                                    >
                                        % of bankroll
                                    </MenuItem>
                                    <MenuItem
                                        value="absolute"
                                        sx={{ fontSize: '12px' }}
                                    >
                                        Absolute $
                                    </MenuItem>
                                </Select>
                            </Ctl>

                            {ui.deployMode === 'fraction' ? (
                                <Ctl
                                    label={`Deploy ${(ui.deployFraction * 100).toFixed(0)}%`}
                                    width={150}
                                >
                                    <Slider
                                        size="small"
                                        min={0}
                                        max={1}
                                        step={0.05}
                                        value={ui.deployFraction}
                                        onChange={(_, v) =>
                                            onChange({
                                                deployFraction: v as number,
                                            })
                                        }
                                        sx={{ width: 150 }}
                                    />
                                </Ctl>
                            ) : (
                                <Ctl label="Total capital ($)">
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={ui.deployAbsoluteText}
                                        onChange={e =>
                                            onChange({
                                                deployAbsoluteText:
                                                    e.target.value,
                                            })
                                        }
                                        sx={tinyInput}
                                    />
                                </Ctl>
                            )}
                        </>
                    )}

                    <Ctl label="Per event" width={150}>
                        <Select
                            size="small"
                            value={ui.oneBetPerEvent ? 'one' : 'all'}
                            onChange={e =>
                                onChange({
                                    oneBetPerEvent: e.target.value === 'one',
                                })
                            }
                            sx={{ ...selectSx, minWidth: 150 }}
                        >
                            <MenuItem value="all" sx={{ fontSize: '12px' }}>
                                Trade all markets
                            </MenuItem>
                            <MenuItem value="one" sx={{ fontSize: '12px' }}>
                                One bet per event
                            </MenuItem>
                        </Select>
                    </Ctl>

                    <Ctl label="Entry reference" width={170}>
                        <Select
                            size="small"
                            value={ui.referencePrice}
                            onChange={e =>
                                onChange({
                                    referencePrice: e.target
                                        .value as ReferencePrice,
                                })
                            }
                            sx={{ ...selectSx, minWidth: 170 }}
                        >
                            {(
                                Object.keys(
                                    REFERENCE_PRICE_LABELS
                                ) as ReferencePrice[]
                            ).map(r => (
                                <MenuItem
                                    key={r}
                                    value={r}
                                    sx={{ fontSize: '12px' }}
                                >
                                    {REFERENCE_PRICE_LABELS[r]}
                                </MenuItem>
                            ))}
                        </Select>
                    </Ctl>
                </Box>

                {/* Algo-specific controls */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-end',
                        gap: 1.5,
                    }}
                >
                    {isProp && (
                        <>
                            <Ctl label="Weight signal" width={150}>
                                <Select
                                    size="small"
                                    value={ui.signal}
                                    onChange={e =>
                                        onChange({
                                            signal: e.target
                                                .value as WeightSignal,
                                        })
                                    }
                                    sx={{ ...selectSx, minWidth: 150 }}
                                >
                                    {(
                                        Object.keys(
                                            SIGNAL_LABELS
                                        ) as WeightSignal[]
                                    ).map(s => (
                                        <MenuItem
                                            key={s}
                                            value={s}
                                            sx={{ fontSize: '12px' }}
                                        >
                                            {SIGNAL_LABELS[s]}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Ctl>

                            <Ctl
                                label={`Concentration α = ${ui.alpha.toFixed(1)}`}
                                width={170}
                            >
                                <Slider
                                    size="small"
                                    min={0}
                                    max={3}
                                    step={0.1}
                                    value={ui.alpha}
                                    onChange={(_, v) =>
                                        onChange({ alpha: v as number })
                                    }
                                    sx={{ width: 170 }}
                                />
                            </Ctl>

                            {isBlend && (
                                <Ctl
                                    label={`Blend β = ${ui.blendBeta.toFixed(2)} (cap↔vol)`}
                                    width={170}
                                >
                                    <Slider
                                        size="small"
                                        min={0}
                                        max={1}
                                        step={0.05}
                                        value={ui.blendBeta}
                                        onChange={(_, v) =>
                                            onChange({ blendBeta: v as number })
                                        }
                                        sx={{ width: 170 }}
                                    />
                                </Ctl>
                            )}
                        </>
                    )}

                    {ui.algoKind === 'volumeFraction' && (
                        <Ctl
                            label={`Stake = ${(ui.volumeFraction * 100).toFixed(2)}% of volume`}
                            width={220}
                        >
                            <Slider
                                size="small"
                                min={0}
                                max={0.05}
                                step={0.0005}
                                value={ui.volumeFraction}
                                onChange={(_, v) =>
                                    onChange({ volumeFraction: v as number })
                                }
                                sx={{ width: 220 }}
                            />
                        </Ctl>
                    )}

                    {ui.algoKind === 'fixedDollars' && (
                        <Ctl label="$ per market">
                            <TextField
                                size="small"
                                type="number"
                                value={ui.fixedDollarsText}
                                onChange={e =>
                                    onChange({
                                        fixedDollarsText: e.target.value,
                                    })
                                }
                                sx={tinyInput}
                            />
                        </Ctl>
                    )}

                    {ui.algoKind === 'fixedShares' && (
                        <Ctl label="Shares per market">
                            <TextField
                                size="small"
                                type="number"
                                value={ui.fixedSharesText}
                                onChange={e =>
                                    onChange({
                                        fixedSharesText: e.target.value,
                                    })
                                }
                                sx={tinyInput}
                            />
                        </Ctl>
                    )}
                </Box>

                {/* Wrappers + fees */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-end',
                        gap: 1.5,
                    }}
                >
                    <Ctl label="Liquidity cap (% vol)">
                        <Tooltip title="Cap each stake at this % of the market's volume. Blank = off.">
                            <TextField
                                size="small"
                                type="number"
                                placeholder="off"
                                value={ui.liquidityCapPctText}
                                onChange={e =>
                                    onChange({
                                        liquidityCapPctText: e.target.value,
                                    })
                                }
                                sx={tinyInput}
                            />
                        </Tooltip>
                    </Ctl>

                    <Ctl label="Per-event cap (% bank)">
                        <Tooltip title="Cap total stake per event at this % of bankroll. Blank = off.">
                            <TextField
                                size="small"
                                type="number"
                                placeholder="off"
                                value={ui.eventCapPctText}
                                onChange={e =>
                                    onChange({
                                        eventCapPctText: e.target.value,
                                    })
                                }
                                sx={tinyInput}
                            />
                        </Tooltip>
                    </Ctl>

                    <Ctl label="Redistribute capped">
                        <Tooltip
                            title={
                                budgeted
                                    ? 'Re-spread dollars freed by a binding cap onto uncapped markets.'
                                    : 'Only applies to budgeted algos.'
                            }
                        >
                            <Box>
                                <Switch
                                    size="small"
                                    checked={ui.redistribute}
                                    disabled={!budgeted}
                                    onChange={e =>
                                        onChange({
                                            redistribute: e.target.checked,
                                        })
                                    }
                                />
                            </Box>
                        </Tooltip>
                    </Ctl>

                    <Ctl label="Fee $ / trade">
                        <TextField
                            size="small"
                            type="number"
                            value={ui.feePerTradeText}
                            onChange={e =>
                                onChange({ feePerTradeText: e.target.value })
                            }
                            sx={tinyInput}
                        />
                    </Ctl>

                    <Ctl label="Fee (bps)">
                        <TextField
                            size="small"
                            type="number"
                            value={ui.feeBpsText}
                            onChange={e =>
                                onChange({ feeBpsText: e.target.value })
                            }
                            sx={tinyInput}
                        />
                    </Ctl>

                    <Ctl label="Min capital ($)">
                        <Tooltip title="Exclude markets with dominant capital below this. Blank = off.">
                            <TextField
                                size="small"
                                type="number"
                                placeholder="off"
                                value={ui.minCapitalThresholdText}
                                onChange={e =>
                                    onChange({
                                        minCapitalThresholdText: e.target.value,
                                    })
                                }
                                sx={tinyInput}
                            />
                        </Tooltip>
                    </Ctl>

                    <Ctl label="Min volume ($)">
                        <Tooltip title="Exclude markets with total volume below this. Blank = off.">
                            <TextField
                                size="small"
                                type="number"
                                placeholder="off"
                                value={ui.minVolumeThresholdText}
                                onChange={e =>
                                    onChange({
                                        minVolumeThresholdText: e.target.value,
                                    })
                                }
                                sx={tinyInput}
                            />
                        </Tooltip>
                    </Ctl>
                </Box>
            </Box>
        </SectionCard>
    );
}

// ---------------------------------------------------------------------------
// Results summary bar
// ---------------------------------------------------------------------------

function Stat({
    label,
    value,
    color,
    hint,
}: {
    label: string;
    value: string;
    color?: string;
    hint?: string;
}) {
    const cell = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                minWidth: 78,
            }}
        >
            <Typography sx={labelSx}>{label}</Typography>
            <Typography
                sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: color ?? 'text.primary',
                }}
            >
                {value}
            </Typography>
        </Box>
    );
    return hint ? <Tooltip title={hint}>{cell}</Tooltip> : cell;
}

function ResultsBar({
    summary,
}: {
    summary: ReturnType<typeof runSimulation>['summary'];
}) {
    const theme = useTheme();
    const pnlColor =
        summary.totalPnl >= 0
            ? theme.palette.success.main
            : theme.palette.error.main;
    const utilOver = summary.utilization > 1.0001;

    return (
        <SectionCard sx={{ mx: 2, mb: 1.5 }}>
            <Box
                sx={{
                    p: 1.5,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2.5,
                    alignItems: 'center',
                }}
            >
                <Stat label="Staked" value={fmtUsd(summary.totalStaked)} />
                <Stat
                    label="Net PnL"
                    value={fmtSignedUsd(summary.totalPnl)}
                    color={pnlColor}
                    hint="After fees"
                />
                <Stat
                    label="ROI"
                    value={fmtSignedPct(summary.roi)}
                    color={pnlColor}
                    hint="Net PnL / staked"
                />
                <Stat label="Hit rate" value={fmtPct(summary.hitRate)} />
                <Stat label="Mean ret" value={fmtSignedPct(summary.meanRet)} />
                <Stat
                    label="Median ret"
                    value={fmtSignedPct(summary.medianRet)}
                />
                <Stat
                    label="Worst ret"
                    value={fmtSignedPct(summary.worstRet)}
                    color={theme.palette.error.main}
                />
                <Stat
                    label="Utilization"
                    value={fmtPct(summary.utilization, 0)}
                    color={utilOver ? theme.palette.warning.main : undefined}
                    hint={
                        utilOver
                            ? 'Staked exceeds bankroll.'
                            : 'Staked / bankroll'
                    }
                />
                <Stat label="Bets" value={fmtNum(summary.nBets)} />
                <Stat
                    label="Capped"
                    value={fmtNum(summary.nCapped)}
                    hint="Stake clipped by a cap"
                />
                <Stat
                    label="Skipped"
                    value={fmtNum(summary.nSkipped)}
                    hint="No entry price → un-simulatable"
                />
                {summary.nExcludedByEvent > 0 && (
                    <Stat
                        label="Event-deduped"
                        value={fmtNum(summary.nExcludedByEvent)}
                        hint="Dropped by one-bet-per-event"
                    />
                )}
                {summary.totalFees > 0 && (
                    <Stat label="Fees" value={fmtUsd(summary.totalFees)} />
                )}
                {summary.nFilteredByThreshold > 0 && (
                    <Stat
                        label="Threshold-cut"
                        value={fmtNum(summary.nFilteredByThreshold)}
                        hint="Excluded by min capital/volume threshold"
                        color={theme.palette.warning.main}
                    />
                )}
            </Box>
        </SectionCard>
    );
}

// ---------------------------------------------------------------------------
// Performance by dimension (recharts)
// ---------------------------------------------------------------------------

interface DimStat {
    name: string;
    pnl: number;
    bets: number;
    staked: number;
    wins: number;
}

function buildDimStats(
    bets: BetResult[],
    marketsById: Map<string, ConvictionMarket>,
    dim: 'category' | 'tag'
): DimStat[] {
    const map = new Map<string, DimStat>();

    const upsert = (key: string, b: BetResult) => {
        if (!map.has(key))
            map.set(key, { name: key, pnl: 0, bets: 0, staked: 0, wins: 0 });
        const s = map.get(key)!;
        if (!b.skipped && b.stakeDollars > 0) {
            s.pnl += b.pnl;
            s.bets += 1;
            s.staked += b.stakeDollars;
            if (b.pnl > 0) s.wins += 1;
        }
    };

    for (const b of bets) {
        const m = marketsById.get(b.marketId);
        if (!m) continue;
        if (dim === 'category') {
            const cat = m.event_category ?? 'Unknown';
            upsert(cat, b);
        } else {
            const tags = m.polymarket_tags ?? [];
            if (tags.length === 0) upsert('(no tag)', b);
            else for (const t of tags) upsert(t, b);
        }
    }

    return Array.from(map.values())
        .filter(s => s.bets > 0)
        .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl));
}

function PnlBarChart({ data, title }: { data: DimStat[]; title: string }) {
    const theme = useTheme();
    if (data.length === 0) return null;

    const displayed = data.slice(0, 16);

    return (
        <Box>
            <Typography
                sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: 'text.secondary',
                    mb: 0.75,
                }}
            >
                {title}
            </Typography>
            <ResponsiveContainer
                width="100%"
                height={Math.max(160, displayed.length * 28)}
            >
                <BarChart
                    data={displayed}
                    layout="vertical"
                    margin={{ left: 8, right: 40, top: 0, bottom: 0 }}
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        horizontal={false}
                        stroke={alpha(theme.palette.divider, 0.5)}
                    />
                    <XAxis
                        type="number"
                        tickFormatter={v => fmtUsd(v)}
                        tick={{
                            fontSize: 10,
                            fill: theme.palette.text.secondary,
                        }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis
                        type="category"
                        dataKey="name"
                        width={110}
                        tick={{
                            fontSize: 10,
                            fill: theme.palette.text.secondary,
                        }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={v =>
                            v.length > 15 ? v.slice(0, 14) + '…' : v
                        }
                    />
                    <RechartsTooltip
                        formatter={(val: number, _name: string, props) => {
                            const d: DimStat = props.payload;
                            return [
                                `PnL: ${fmtSignedUsd(val)}  |  Bets: ${d.bets}  |  Hit: ${d.bets > 0 ? Math.round((d.wins / d.bets) * 100) : 0}%  |  Staked: ${fmtUsd(d.staked)}`,
                                '',
                            ];
                        }}
                        contentStyle={{
                            fontSize: 11,
                            background: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 4,
                        }}
                        cursor={{
                            fill: alpha(theme.palette.action.hover, 0.5),
                        }}
                    />
                    <Bar dataKey="pnl" radius={[0, 3, 3, 0]}>
                        {displayed.map((entry, i) => (
                            <Cell
                                key={i}
                                fill={
                                    entry.pnl >= 0
                                        ? alpha(
                                              theme.palette.success.main,
                                              0.75
                                          )
                                        : alpha(theme.palette.error.main, 0.75)
                                }
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}

function PerformanceOverview({
    bets,
    marketsById,
}: {
    bets: BetResult[];
    marketsById: Map<string, ConvictionMarket>;
}) {
    const catData = useMemo(
        () => buildDimStats(bets, marketsById, 'category'),
        [bets, marketsById]
    );
    const tagData = useMemo(
        () => buildDimStats(bets, marketsById, 'tag'),
        [bets, marketsById]
    );

    const placed = bets.filter(b => !b.skipped && b.stakeDollars > 0);
    if (placed.length === 0) return null;

    return (
        <SectionCard sx={{ mx: 2, mb: 1.5 }}>
            <Box sx={{ p: 1.5 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, mb: 1.5 }}>
                    Performance breakdown
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 3,
                    }}
                >
                    <PnlBarChart data={catData} title="By category" />
                    <PnlBarChart data={tagData} title="By tag" />
                </Box>

                {/* Summary table */}
                {catData.length > 0 && (
                    <Box sx={{ mt: 2, overflowX: 'auto', ...hoverScrollbar }}>
                        <Box
                            component="table"
                            sx={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '11px',
                            }}
                        >
                            <Box component="thead">
                                <Box
                                    component="tr"
                                    sx={{
                                        borderBottom: t =>
                                            `1px solid ${t.palette.divider}`,
                                    }}
                                >
                                    {[
                                        'Category',
                                        'Bets',
                                        'Staked',
                                        'PnL',
                                        'ROI',
                                        'Hit %',
                                    ].map(h => (
                                        <Box
                                            component="th"
                                            key={h}
                                            sx={{
                                                ...labelSx,
                                                px: 1,
                                                py: 0.5,
                                                textAlign:
                                                    h === 'Category'
                                                        ? 'left'
                                                        : 'right',
                                            }}
                                        >
                                            {h}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {catData.map(d => (
                                    <Box
                                        component="tr"
                                        key={d.name}
                                        sx={{
                                            borderBottom: t =>
                                                `1px solid ${alpha(t.palette.divider, 0.4)}`,
                                        }}
                                    >
                                        <Box
                                            component="td"
                                            sx={{ px: 1, py: 0.5 }}
                                        >
                                            {d.name}
                                        </Box>
                                        <Box
                                            component="td"
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {d.bets}
                                        </Box>
                                        <Box
                                            component="td"
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {fmtUsd(d.staked)}
                                        </Box>
                                        <Box
                                            component="td"
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                                color: t =>
                                                    d.pnl >= 0
                                                        ? t.palette.success.main
                                                        : t.palette.error.main,
                                            }}
                                        >
                                            {fmtSignedUsd(d.pnl)}
                                        </Box>
                                        <Box
                                            component="td"
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                                color: t =>
                                                    d.pnl >= 0
                                                        ? t.palette.success.main
                                                        : t.palette.error.main,
                                            }}
                                        >
                                            {d.staked > 0
                                                ? fmtSignedPct(d.pnl / d.staked)
                                                : '—'}
                                        </Box>
                                        <Box
                                            component="td"
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {d.bets > 0
                                                ? Math.round(
                                                      (d.wins / d.bets) * 100
                                                  ) + '%'
                                                : '—'}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </SectionCard>
    );
}

// ---------------------------------------------------------------------------
// Trader scores expansion panel
// ---------------------------------------------------------------------------

interface TraderScore {
    address: string;
    side: 'YES' | 'NO';
    net_shares: number;
    net_exposure_usd: number;
    avg_entry_price: number | null;
    implied_probability: number | null;
}

function TraderExpansionPanel({ market }: { market: ConvictionMarket }) {
    const theme = useTheme();

    const traderScores: TraderScore[] = useMemo(() => {
        const bd = market.breakdown as
            | { trader_scores?: TraderScore[] }
            | null
            | undefined;
        return bd?.trader_scores ?? [];
    }, [market.breakdown]);

    const yes = traderScores
        .filter(t => t.side === 'YES')
        .sort((a, b) => b.net_exposure_usd - a.net_exposure_usd);
    const no = traderScores
        .filter(t => t.side === 'NO')
        .sort((a, b) => b.net_exposure_usd - a.net_exposure_usd);

    if (traderScores.length === 0) {
        return (
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                }}
            >
                <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                    No trader breakdown available — enable "Trader scores" in
                    market filters.
                </Typography>
            </Box>
        );
    }

    const traderTh = {
        fontSize: '9px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'text.disabled',
        px: 0.75,
        py: 0.5,
        textAlign: 'right' as const,
    };
    const traderTd = {
        fontSize: '11px',
        fontFamily: 'monospace',
        px: 0.75,
        py: 0.375,
        textAlign: 'right' as const,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        verticalAlign: 'middle' as const,
    };

    const renderTable = (traders: TraderScore[], side: 'YES' | 'NO') => {
        const sideColor =
            side === 'YES'
                ? theme.palette.success.main
                : theme.palette.error.main;
        return (
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: sideColor,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                    }}
                >
                    {side} ({traders.length})
                </Typography>
                {traders.length === 0 ? (
                    <Typography
                        sx={{
                            fontSize: '10px',
                            color: 'text.disabled',
                            fontStyle: 'italic',
                        }}
                    >
                        none
                    </Typography>
                ) : (
                    <Box
                        component="table"
                        sx={{ width: '100%', borderCollapse: 'collapse' }}
                    >
                        <Box component="thead">
                            <Box component="tr">
                                <Box
                                    component="th"
                                    sx={{ ...traderTh, textAlign: 'left' }}
                                >
                                    Address
                                </Box>
                                <Box component="th" sx={traderTh}>
                                    Exposure
                                </Box>
                                <Box component="th" sx={traderTh}>
                                    Shares
                                </Box>
                                <Box component="th" sx={traderTh}>
                                    Avg entry
                                </Box>
                                <Box component="th" sx={traderTh}>
                                    Implied
                                </Box>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {traders.map(t => (
                                <Box component="tr" key={t.address}>
                                    <Box
                                        component="td"
                                        sx={{
                                            ...traderTd,
                                            textAlign: 'left',
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        <Box
                                            component="a"
                                            href={`/markets/trader/${t.address}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e: React.MouseEvent) =>
                                                e.stopPropagation()
                                            }
                                            sx={{
                                                color: 'primary.main',
                                                textDecoration: 'none',
                                                fontSize: '11px',
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                },
                                            }}
                                        >
                                            {truncAddr(t.address)}
                                        </Box>
                                    </Box>
                                    <Box component="td" sx={traderTd}>
                                        {fmtUsd(t.net_exposure_usd)}
                                    </Box>
                                    <Box component="td" sx={traderTd}>
                                        {fmtNum(t.net_shares, 1)}
                                    </Box>
                                    <Box component="td" sx={traderTd}>
                                        {t.avg_entry_price != null
                                            ? fmtPct(t.avg_entry_price)
                                            : '—'}
                                    </Box>
                                    <Box component="td" sx={traderTd}>
                                        {t.implied_probability != null
                                            ? fmtPct(t.implied_probability)
                                            : '—'}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Box
            sx={{
                px: 2,
                py: 1.25,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                display: 'flex',
                gap: 3,
            }}
        >
            {renderTable(yes, 'YES')}
            <Box sx={{ width: '1px', bgcolor: 'divider', flexShrink: 0 }} />
            {renderTable(no, 'NO')}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Bets table — screener columns left, sim columns right
// ---------------------------------------------------------------------------

const thSx = {
    fontSize: '9px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'text.secondary',
    textAlign: 'right',
    px: 0.75,
    py: 0.75,
    whiteSpace: 'nowrap',
} as const;

const tdSx = {
    fontSize: '11px',
    px: 0.75,
    py: 0.625,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
} as const;

// Compact capital split bar (same as screener)
function CapitalSplitBar({ yes, no }: { yes: number; no: number }) {
    const theme = useTheme();
    const total = yes + no;
    const yesFrac = total > 0 ? yes / total : 0.5;
    return (
        <Box sx={{ mt: '2px' }}>
            <Box
                sx={{
                    display: 'flex',
                    height: 3,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: alpha(theme.palette.text.disabled, 0.15),
                }}
            >
                <Box
                    sx={{
                        width: `${yesFrac * 100}%`,
                        bgcolor: alpha(theme.palette.success.main, 0.75),
                    }}
                />
                <Box
                    sx={{
                        width: `${(1 - yesFrac) * 100}%`,
                        bgcolor: alpha(theme.palette.error.main, 0.6),
                    }}
                />
            </Box>
        </Box>
    );
}

function SideBadge({ side }: { side: string | null }) {
    const theme = useTheme();
    if (!side)
        return (
            <Typography sx={{ fontSize: '10px', color: 'text.disabled' }}>
                —
            </Typography>
        );
    const color =
        side === 'YES' ? theme.palette.success.main : theme.palette.error.main;
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                fontSize: '9px',
                fontWeight: 700,
                color,
                border: `1px solid ${alpha(color, 0.5)}`,
                bgcolor: alpha(color, 0.08),
                borderRadius: 0.75,
                px: 0.5,
                py: '1px',
            }}
        >
            {side}
        </Box>
    );
}

function SignalBadge({ open, total }: { open: number; total: number }) {
    const theme = useTheme();
    if (total === 0)
        return (
            <Typography sx={{ fontSize: '10px', color: 'text.disabled' }}>
                —
            </Typography>
        );
    return (
        <Typography
            sx={{
                fontSize: '10px',
                fontFamily: 'monospace',
                color: open > 0 ? theme.palette.warning.main : 'text.secondary',
            }}
        >
            {open}/{total}
        </Typography>
    );
}

function BetsTable({
    bets,
    marketsById,
    eventCounts,
    refLabel,
}: {
    bets: BetResult[];
    marketsById: Map<string, ConvictionMarket>;
    eventCounts: Map<string, number>;
    refLabel: string;
}) {
    const theme = useTheme();
    const [expandedId, setExpandedId] = useState<string | null>(null);

    return (
        <Box
            component="table"
            sx={{ width: '100%', borderCollapse: 'collapse' }}
        >
            <Box component="thead">
                <Box
                    component="tr"
                    sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
                >
                    {/* Left group: expand + screener intel */}
                    <Box component="th" sx={{ ...thSx, width: 24 }} />
                    {/* Event-sibling dot */}
                    <Box component="th" sx={{ ...thSx, width: 16 }} />
                    <Box
                        component="th"
                        sx={{ ...thSx, textAlign: 'left', minWidth: 220 }}
                    >
                        Market
                    </Box>
                    <Box
                        component="th"
                        sx={{ ...thSx, textAlign: 'center', width: 48 }}
                    >
                        Side
                    </Box>
                    <Box component="th" sx={{ ...thSx, width: 90 }}>
                        Capital
                    </Box>
                    <Box component="th" sx={{ ...thSx, width: 56 }}>
                        Traders
                    </Box>
                    <Box component="th" sx={{ ...thSx, width: 64 }}>
                        Mkt%
                    </Box>
                    <Box component="th" sx={{ ...thSx, width: 90 }}>
                        Implied%
                    </Box>
                    <Box component="th" sx={{ ...thSx, width: 90 }}>
                        Avg entry
                    </Box>
                    <Box
                        component="th"
                        sx={{
                            ...thSx,
                            width: 56,
                            borderRight: t => `2px solid ${t.palette.divider}`,
                        }}
                    >
                        Signals
                    </Box>

                    {/* Right group: sim columns */}
                    <Box component="th" sx={{ ...thSx, width: 40 }}>
                        Days
                    </Box>
                    <Box component="th" sx={{ ...thSx, width: 56 }}>
                        <Tooltip title={`Entry reference: ${refLabel}`}>
                            <span>Entry</span>
                        </Tooltip>
                    </Box>
                    <Box component="th" sx={{ ...thSx, width: 56 }}>
                        Mark
                    </Box>
                    <Box component="th" sx={{ ...thSx, width: 64 }}>
                        <Tooltip title="Dominant capital / market volume">
                            <span>Cap/Vol</span>
                        </Tooltip>
                    </Box>
                    <Box component="th" sx={{ ...thSx, width: 72 }}>
                        Stake
                    </Box>
                    <Box component="th" sx={{ ...thSx, width: 56 }}>
                        Shares
                    </Box>
                    <Box component="th" sx={{ ...thSx, width: 72 }}>
                        PnL
                    </Box>
                    <Box component="th" sx={{ ...thSx, width: 64 }}>
                        Return
                    </Box>
                </Box>
            </Box>
            <Box component="tbody">
                {bets.map(b => {
                    const m = marketsById.get(b.marketId);
                    const siblings = b.eventId
                        ? eventCounts.get(b.eventId) ?? 0
                        : 0;
                    const days = daysRunning(m?.start_date ?? null);
                    const retColor =
                        b.skipped || b.stakeDollars === 0
                            ? 'text.disabled'
                            : b.pnl >= 0
                              ? theme.palette.success.main
                              : theme.palette.error.main;

                    const hasBreakdown = !!m?.breakdown;
                    const isExpanded = expandedId === b.marketId;

                    // Screener-derived values
                    const dominantCapital = m
                        ? m.dominant_side === 'NO'
                            ? m.capital_usd_no
                            : m.capital_usd_yes
                        : null;
                    const traders = m
                        ? (m.n_traders_yes ?? 0) + (m.n_traders_no ?? 0)
                        : null;

                    // Outcome probability
                    const outcomeMarketProb = m
                        ? m.dominant_side === 'NO'
                            ? m.current_yes_prob != null
                                ? 1 - m.current_yes_prob
                                : null
                            : m.current_yes_prob
                        : null;
                    const outcomeImpliedProb = m
                        ? m.dominant_side === 'NO'
                            ? m.raw_implied != null
                                ? 1 - m.raw_implied
                                : null
                            : m.raw_implied
                        : null;
                    const impliedDelta =
                        outcomeImpliedProb != null && outcomeMarketProb != null
                            ? outcomeMarketProb - outcomeImpliedProb
                            : null;

                    const ga = (
                        m?.breakdown as {
                            group_aggregation?: {
                                cw_avg_entry_price_yes?: number | null;
                                cw_avg_entry_price_no?: number | null;
                            };
                        } | null
                    )?.group_aggregation;
                    const avgEntry = ga
                        ? (m!.dominant_side === 'NO'
                              ? ga.cw_avg_entry_price_no
                              : ga.cw_avg_entry_price_yes) ?? null
                        : null;
                    const entryDelta =
                        avgEntry != null && outcomeMarketProb != null
                            ? outcomeMarketProb - avgEntry
                            : null;

                    const eventSlug = m?.event_slug;

                    return (
                        <>
                            <Box
                                component="tr"
                                key={b.marketId}
                                sx={{
                                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                                    opacity: b.skipped ? 0.5 : 1,
                                    '&:hover td': { bgcolor: 'action.hover' },
                                    cursor: hasBreakdown
                                        ? 'pointer'
                                        : 'default',
                                }}
                                onClick={() =>
                                    hasBreakdown &&
                                    setExpandedId(prev =>
                                        prev === b.marketId ? null : b.marketId
                                    )
                                }
                            >
                                {/* Expand chevron */}
                                <Box
                                    component="td"
                                    sx={{
                                        ...tdSx,
                                        textAlign: 'center',
                                        width: 24,
                                    }}
                                >
                                    {hasBreakdown &&
                                        (isExpanded ? (
                                            <ExpandMoreIcon
                                                sx={{
                                                    fontSize: 14,
                                                    color: 'primary.main',
                                                }}
                                            />
                                        ) : (
                                            <ChevronRightIcon
                                                sx={{
                                                    fontSize: 14,
                                                    color: 'text.disabled',
                                                }}
                                            />
                                        ))}
                                </Box>

                                {/* Event sibling dot */}
                                <Box component="td" sx={{ ...tdSx, width: 16 }}>
                                    {b.eventId && siblings > 1 && (
                                        <Tooltip
                                            title={`${siblings} markets from this event`}
                                        >
                                            <Box
                                                sx={{
                                                    width: 7,
                                                    height: 7,
                                                    borderRadius: '50%',
                                                    bgcolor: `hsl(${eventHue(b.eventId)} 70% 55%)`,
                                                    display: 'inline-block',
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>

                                {/* Market question — click navigates to internal event view */}
                                <Box
                                    component="td"
                                    sx={{
                                        ...tdSx,
                                        textAlign: 'left',
                                        maxWidth: 240,
                                    }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <Tooltip
                                        title={
                                            m?.event_title
                                                ? `${m.market_question ?? b.marketId}  ·  ${m.event_title}`
                                                : m?.market_question ??
                                                  b.marketId
                                        }
                                        placement="top-start"
                                        enterDelay={300}
                                        arrow
                                    >
                                        <Box
                                            component={eventSlug ? 'a' : 'span'}
                                            href={
                                                eventSlug
                                                    ? `/markets/event/${eventSlug}`
                                                    : undefined
                                            }
                                            target={
                                                eventSlug ? '_blank' : undefined
                                            }
                                            rel={
                                                eventSlug
                                                    ? 'noopener noreferrer'
                                                    : undefined
                                            }
                                            sx={{
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: '11px',
                                                fontWeight: 500,
                                                color: eventSlug
                                                    ? 'primary.main'
                                                    : 'text.primary',
                                                textDecoration: 'none',
                                                '&:hover': eventSlug
                                                    ? {
                                                          textDecoration:
                                                              'underline',
                                                      }
                                                    : {},
                                            }}
                                        >
                                            {m?.market_question ?? b.marketId}
                                        </Box>
                                    </Tooltip>
                                    {m?.conviction_outcome && (
                                        <Typography
                                            sx={{
                                                fontSize: '10px',
                                                color: 'text.secondary',
                                            }}
                                        >
                                            on {m.conviction_outcome}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Side badge */}
                                <Box
                                    component="td"
                                    sx={{ ...tdSx, textAlign: 'center' }}
                                >
                                    <SideBadge
                                        side={
                                            m?.dominant_side ?? b.side ?? null
                                        }
                                    />
                                </Box>

                                {/* Capital + split bar */}
                                <Box component="td" sx={{ ...tdSx }}>
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {fmtUsd(dominantCapital)}
                                    </Typography>
                                    {m && (
                                        <CapitalSplitBar
                                            yes={m.capital_usd_yes}
                                            no={m.capital_usd_no}
                                        />
                                    )}
                                </Box>

                                {/* Traders */}
                                <Box
                                    component="td"
                                    sx={{ ...tdSx, fontFamily: 'monospace' }}
                                >
                                    {traders != null ? fmtNum(traders) : '—'}
                                </Box>

                                {/* Market % */}
                                <Box
                                    component="td"
                                    sx={{ ...tdSx, fontFamily: 'monospace' }}
                                >
                                    {fmtPct(outcomeMarketProb)}
                                </Box>

                                {/* Implied % with delta */}
                                <Box
                                    component="td"
                                    sx={{
                                        ...tdSx,
                                        fontFamily: 'monospace',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {fmtPct(outcomeImpliedProb)}
                                    {impliedDelta != null && (
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 0.5,
                                                fontSize: '10px',
                                                color:
                                                    impliedDelta >= 0
                                                        ? theme.palette.success
                                                              .main
                                                        : theme.palette.error
                                                              .main,
                                            }}
                                        >
                                            ({impliedDelta >= 0 ? '+' : ''}
                                            {(impliedDelta * 100).toFixed(0)}%)
                                        </Box>
                                    )}
                                </Box>

                                {/* Avg entry with delta */}
                                <Box
                                    component="td"
                                    sx={{
                                        ...tdSx,
                                        fontFamily: 'monospace',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {fmtPct(avgEntry)}
                                    {entryDelta != null && (
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 0.5,
                                                fontSize: '10px',
                                                color:
                                                    entryDelta >= 0
                                                        ? theme.palette.success
                                                              .main
                                                        : theme.palette.error
                                                              .main,
                                            }}
                                        >
                                            ({entryDelta >= 0 ? '+' : ''}
                                            {(entryDelta * 100).toFixed(0)}%)
                                        </Box>
                                    )}
                                </Box>

                                {/* Signals — right border separates screener from sim columns */}
                                <Box
                                    component="td"
                                    sx={{
                                        ...tdSx,
                                        borderRight: t =>
                                            `2px solid ${t.palette.divider}`,
                                    }}
                                >
                                    <SignalBadge
                                        open={m?.open_signal_count ?? 0}
                                        total={m?.signal_count ?? 0}
                                    />
                                </Box>

                                {/* Days */}
                                <Box component="td" sx={tdSx}>
                                    {days != null ? Math.round(days) : '—'}
                                </Box>

                                {/* Entry ref price */}
                                <Box component="td" sx={tdSx}>
                                    {fmtPct(b.entryPrice)}
                                </Box>

                                {/* Mark price */}
                                <Box component="td" sx={tdSx}>
                                    {fmtPct(b.markPrice)}
                                </Box>

                                {/* Cap/Vol */}
                                <Box component="td" sx={tdSx}>
                                    {b.capitalToVolume != null
                                        ? fmtPct(b.capitalToVolume, 2)
                                        : '—'}
                                </Box>

                                {/* Stake */}
                                <Box component="td" sx={tdSx}>
                                    {b.skipped ? (
                                        <Chip
                                            label="skip"
                                            size="small"
                                            sx={{ height: 16, fontSize: '9px' }}
                                        />
                                    ) : b.filteredByThreshold ? (
                                        <Chip
                                            label="filtered"
                                            size="small"
                                            color="warning"
                                            sx={{ height: 16, fontSize: '9px' }}
                                        />
                                    ) : (
                                        <Box component="span">
                                            {fmtUsd(b.stakeDollars)}
                                            {b.capped && (
                                                <Tooltip title="Stake clipped by a cap">
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            color: theme.palette
                                                                .warning.main,
                                                            ml: 0.5,
                                                            fontSize: '10px',
                                                        }}
                                                    >
                                                        ▲
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                {/* Shares */}
                                <Box component="td" sx={tdSx}>
                                    {b.skipped ? '—' : fmtNum(b.stakeShares)}
                                </Box>

                                {/* PnL */}
                                <Box
                                    component="td"
                                    sx={{ ...tdSx, color: retColor }}
                                >
                                    {b.skipped ? '—' : fmtSignedUsd(b.pnl)}
                                </Box>

                                {/* Return */}
                                <Box
                                    component="td"
                                    sx={{ ...tdSx, color: retColor }}
                                >
                                    {b.skipped || b.stakeDollars === 0
                                        ? '—'
                                        : fmtSignedPct(b.ret)}
                                </Box>
                            </Box>

                            {/* Trader expansion row */}
                            {isExpanded && m && (
                                <Box
                                    component="tr"
                                    key={`${b.marketId}-expand`}
                                >
                                    <Box
                                        component="td"
                                        colSpan={99}
                                        sx={{
                                            p: 0,
                                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                                        }}
                                    >
                                        <TraderExpansionPanel market={m} />
                                    </Box>
                                </Box>
                            )}
                        </>
                    );
                })}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Section label
// ---------------------------------------------------------------------------

function SectionLabel({
    title,
    subtitle,
}: {
    title: string;
    subtitle?: string;
}) {
    return (
        <Box sx={{ px: 2, pt: 0.5, pb: 0.75 }}>
            <Typography
                sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.03em',
                }}
            >
                {title}
            </Typography>
            {subtitle && (
                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SimulationView() {
    // Dynamic groups: merge API groups with known defaults so new groups appear
    // automatically and the hardcoded fallbacks are always present.
    // Mirrors the same pattern in ConvictionScreenerView.
    const { data: convictionGroupsData } = useConvictionGroups();
    const groups = useMemo(() => {
        const fromApi = convictionGroupsData?.groups ?? [];
        // Static fallbacks ensure these are always present even before the API responds
        return Array.from(new Set([...fromApi]));
    }, [convictionGroupsData]);

    // Filters (debounced) → hook params.
    const [filters, setFilters] = useState<MarketFilterValues>(DEFAULT_FILTERS);
    const debouncedFilters = useDebounced(filters);
    const params = useMemo<ConvictionScreenerParams>(
        () => ({ ...filtersToParams(debouncedFilters) }),
        [debouncedFilters]
    );
    const { data, loading, error } = useConvictionScreener(params);

    // Sizing config.
    const [ui, setUi] = useState<SimUiState>(DEFAULT_SIM_UI);
    const config = useMemo(() => buildConfig(ui), [ui]);

    const markets = useMemo(() => data?.markets ?? [], [data]);

    const simMarkets = useMemo(
        () => markets.map(m => toSimMarket(m, ui.referencePrice)),
        [markets, ui.referencePrice]
    );
    const result = useMemo(
        () => runSimulation(simMarkets, config),
        [simMarkets, config]
    );

    const marketsById = useMemo(() => {
        const map = new Map<string, ConvictionMarket>();
        for (const m of markets) map.set(m.market_id, m);
        return map;
    }, [markets]);

    const eventCounts = useMemo(() => {
        const map = new Map<string, number>();
        for (const m of markets) {
            if (m.event_id) map.set(m.event_id, (map.get(m.event_id) ?? 0) + 1);
        }
        return map;
    }, [markets]);

    const sortedBets = useMemo(() => {
        return [...result.bets].sort((a, b) => {
            // placed bets first, then threshold-cut, then skipped
            const rankA = a.skipped ? 2 : a.filteredByThreshold ? 1 : 0;
            const rankB = b.skipped ? 2 : b.filteredByThreshold ? 1 : 0;
            if (rankA !== rankB) return rankA - rankB;
            return b.stakeDollars - a.stakeDollars;
        });
    }, [result.bets]);

    const onFilterChange = (patch: Partial<MarketFilterValues>) =>
        setFilters(prev => ({ ...prev, ...patch }));
    const onUiChange = (patch: Partial<SimUiState>) =>
        setUi(prev => ({ ...prev, ...patch }));

    return (
        <Box sx={{ pb: 4 }}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
                    Position-Sizing Simulation
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    Pick a market universe, then test sizing settings against
                    it. Change the selection or strategy to compare PnL.
                </Typography>
            </Box>

            <SectionLabel
                title="1 · Market selection"
                subtitle="Group, ranking, tags, categories, price, duration, trader scores."
            />
            <SectionCard sx={{ mx: 2, mb: 2 }}>
                <MarketFilters
                    groups={groups}
                    value={filters}
                    onChange={onFilterChange}
                    onReset={() => setFilters(DEFAULT_FILTERS)}
                    resultCount={data?.markets.length ?? null}
                    loading={loading}
                />
            </SectionCard>

            <SectionLabel
                title="2 · Simulation settings"
                subtitle="Bankroll, sizing algorithm, caps and fees."
            />
            <SimControls ui={ui} onChange={onUiChange} />

            <SectionLabel title="Results" />
            <ResultsBar summary={result.summary} />

            {error && (
                <Typography
                    sx={{ mx: 2, fontSize: '12px', color: 'error.main' }}
                >
                    {error}
                </Typography>
            )}

            <SectionLabel
                title="Performance breakdown"
                subtitle="PnL by category and tag for this strategy across the selected universe."
            />
            <PerformanceOverview bets={sortedBets} marketsById={marketsById} />

            <SectionLabel
                title="Bets"
                subtitle="Screener intel left · simulation outputs right · click row to expand trader positions"
            />
            <SectionCard sx={{ mx: 2 }}>
                <Box sx={{ p: 1, overflowX: 'auto', ...hoverScrollbar }}>
                    {loading && markets.length === 0 ? (
                        <Box
                            sx={{
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            {Array.from({ length: 8 }).map((_, i) => (
                                <Skeleton
                                    key={i}
                                    variant="rectangular"
                                    height={28}
                                />
                            ))}
                        </Box>
                    ) : sortedBets.length === 0 ? (
                        <Typography
                            sx={{
                                p: 2,
                                fontSize: '12px',
                                color: 'text.secondary',
                            }}
                        >
                            No markets match these filters.
                        </Typography>
                    ) : (
                        <BetsTable
                            bets={sortedBets}
                            marketsById={marketsById}
                            eventCounts={eventCounts}
                            refLabel={REFERENCE_PRICE_LABELS[ui.referencePrice]}
                        />
                    )}
                </Box>
            </SectionCard>
        </Box>
    );
}

export default SimulationView;
